import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { UserIcon, QuestionMarkCircleIcon, UserGroupIcon, ShareIcon, CheckCircleIcon, ClockIcon, ExclamationCircleIcon, Bars3Icon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ArrowPathIcon as SwitchHorizontalIcon } from '@heroicons/react/24/outline';
import { useAuthContext } from '../../auth/context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import SummarySection from './SummarySection';
import ExpenseSection from './ExpenseSection';
import DebtSection from './DebtSection';
import SavingsSection from './SavingsSection';
import IncomeSection from './IncomeSection';
import BudgetSection from './BudgetSection';
import AssetsSection from './AssetsSection';
import InvestmentsSection from './InvestmentsSection';
import MobileViewSelector from '../../../components/shared/MobileViewSelector';
import { 
  getCashFlowSummary, 
  getExpenses, 
  getDebts, 
  getSavings,
  getAssets
} from '../services/cashFlowService';
import axiosInstance from '../../../utils/axiosConfig';
import useIncome from '../../income/hooks/useIncome';
import { calculateAllIncomeTotals } from '../../income/utils/incomeUtils';

const CashFlowDashboard = ({ initialTab }) => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Map tab names to indices
  const tabMap = {
    'summary': 0,
    'income': 1,
    'expenses': 2,
    'budget': 3,
    'debt': 4,
    'savings': 5,
    'assets': 6,
    'investments': 7
  };
  
  // Get initial tab from props or URL
  const getInitialTabIndex = () => {
    if (initialTab && tabMap[initialTab] !== undefined) {
      return tabMap[initialTab];
    }
    // Check URL path for tab
    const pathname = location.pathname;
    if (pathname.includes('/income')) return 1;
    if (pathname.includes('/expenses')) return 2;
    if (pathname.includes('/budget')) return 3;
    if (pathname.includes('/debt')) return 4;
    if (pathname.includes('/savings')) return 5;
    if (pathname.includes('/assets')) return 6;
    if (pathname.includes('/investments')) return 7;
    return 0; // Default to summary
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTabIndex);
  const [expenses, setExpenses] = useState([]);
  const [debts, setDebts] = useState([]);
  const [savings, setSavings] = useState([]);
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partnerPermissions, setPartnerPermissions] = useState(null);
  const [partnerName, setPartnerName] = useState('');
  const [activeViewTab, setActiveViewTab] = useState('my'); // 'my', 'our', 'partner'
  const [showTabMenu, setShowTabMenu] = useState(false); // For mobile tab navigation hamburger menu
  const [showMoreTabsMenu, setShowMoreTabsMenu] = useState(false); // For overflow tabs dropdown
  
  // Derive isPartnerMode and isOurMode from activeViewTab
  // 'my' = user's own data only
  // 'our' = combined user + partner data  
  // 'partner' = partner's data only
  const isPartnerMode = activeViewTab === 'partner';
  const isOurMode = activeViewTab === 'our';

  // Get income data using the useIncome hook with correct modes
  const { incomes, fetchIncomes } = useIncome({ 
    isPartnerMode, 
    includePartner: isOurMode  // ⭐ FIX: useIncome expects 'includePartner', not 'isOurMode'!
  });

  // Check if user has a partner and get permissions
  const hasPartner = user?.partner || user?.partnerId || user?.createdPartner;
  const partnerDisplayName = user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner';
  
  // Debug: Log partner info
  console.log('👥 Partner Info Debug:', {
    hasPartner,
    userPartner: user?.partner,
    userCreatedPartner: user?.createdPartner,
    userPartnerId: user?.partnerId,
    partnerDisplayName,
    currentUser: user?.firstName
  });

  // Fetch partner permissions
  useEffect(() => {
    const fetchPartnerPermissions = async () => {
      if (!hasPartner) return;
      
      try {
        // IMPORTANT: We need to check the OWNER's permissions, not the partner's
        // If we ARE the partner, we need to check the owner's permissions
        // If we are the owner, we need to check our own permissions for the partner
        
        let ownerUserId;
        if (user?.partnerId) {
          // Current user is the partner, check the owner's permissions
          ownerUserId = user.partnerId;
        } else if (user?.createdPartner) {
          // Current user is the owner, check our own permissions
          ownerUserId = user._id;
        } else {
          console.log('No partner relationship found');
          return;
        }

        console.log('🔍 Fetching partner permissions from owner:', ownerUserId);
        const response = await axiosInstance.get(`/api/user-sharing/partner-permissions/${ownerUserId}`);
        if (response.data.success) {
          setPartnerPermissions(response.data.permissions);
          console.log('✅ Partner permissions retrieved:', response.data.permissions);
        }
      } catch (error) {
        console.error('❌ Error fetching partner permissions:', error);
        console.error('Error details:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
      }
    };

    fetchPartnerPermissions();
  }, [hasPartner, user]);

  // Update tab when URL changes
  useEffect(() => {
    const newTab = getInitialTabIndex();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.pathname]);

  // Close tab menu when clicking outside
  useEffect(() => {
    if (showTabMenu) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.cashflow-tab-navigation')) {
          setShowTabMenu(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showTabMenu]);

  // Close more tabs menu when clicking outside
  useEffect(() => {
    if (showMoreTabsMenu) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.cashflow-tab-navigation')) {
          setShowMoreTabsMenu(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showMoreTabsMenu]);

  // Recalculate summary when incomes change (handles mode switching)
  // Uses calculateAllIncomeTotals from incomeUtils.js to ensure consistency
  // with the Income page calculations
  useEffect(() => {
    // Use the unified calculation function from incomeUtils.js
    // This ensures Summary page shows the same Net Income as the Income page
    const incomeTotals = calculateAllIncomeTotals(incomes);
    const monthlyGross = incomeTotals.gross;
    const monthlyNet = incomeTotals.net;

    console.log('🔄 Updating summary income totals (using calculateAllIncomeTotals):');
    console.log('  Mode:', isOurMode ? 'Our' : isPartnerMode ? 'Partner' : 'My');
    console.log('  Income Count:', incomes.length);
    console.log('  Monthly Gross:', monthlyGross);
    console.log('  Monthly Net:', monthlyNet);
    console.log('  Monthly Tax:', incomeTotals.tax);

    // Update summary with new income totals
    setSummary(prev => {
      if (!prev) {
        // If summary doesn't exist yet, create it with income values
        return {
          monthlyGrossIncome: monthlyGross,
          monthlyNetIncome: monthlyNet,
          totalMonthlyExpenses: 0,
          totalDebt: 0,
          totalMonthlyDebtPayments: 0,
          totalSavings: 0,
          totalSavingsTarget: 0,
          totalAssetValue: 0,
          netWorth: 0
        };
      }
      // Otherwise, update existing summary
      return {
        ...prev,
        monthlyGrossIncome: monthlyGross,
        monthlyNetIncome: monthlyNet
      };
    });
  }, [incomes, isOurMode, isPartnerMode]);

  // Load data based on current mode (user or partner)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Determine if we need to use partner access based on activeViewTab
        // Only use partner access for the 'partner' tab, not 'our' tab
        const usePartnerAccess = activeViewTab === 'partner' && hasPartner;
        
        console.log('🔄 Loading cash flow data:', {
          activeViewTab,
          hasPartner,
          usePartnerAccess,
          partnerPermissions
        });

        if (usePartnerAccess) {
          // Check if partner has granted cashflow access
          if (!partnerPermissions?.hasFullCashflowAccess) {
            throw new Error('Partner has not granted access to their cashflow data');
          }

          // Load partner data using cashflow API routes with partner headers
          // Using query parameter instead of custom header to avoid Railway CORS issues
          const [summaryData, expensesData, debtsData, savingsData, assetsData, incomesData] = await Promise.all([
            axiosInstance.get('/api/cashflow/summary?partner=true'),
            axiosInstance.get('/api/cashflow/expenses?partner=true'),
            axiosInstance.get('/api/cashflow/debts?partner=true'),
            axiosInstance.get('/api/cashflow/savings?partner=true'),
            axiosInstance.get('/api/cashflow/assets?partner=true').catch(() => ({ data: { success: true, data: [] } })), // Fallback for assets
            axiosInstance.get('/api/cashflow/income?partner=true')
          ]);

          console.log('✅ Partner data loaded successfully');
          
          // Calculate income totals from partner data using the unified calculation function
          const partnerIncomes = Array.isArray(incomesData.data?.data) ? incomesData.data.data : [];
          const partnerIncomeTotals = calculateAllIncomeTotals(partnerIncomes);
          const monthlyGross = partnerIncomeTotals.gross;
          const monthlyNet = partnerIncomeTotals.net;

          console.log('📊 Partner income totals (using calculateAllIncomeTotals):', {
            gross: monthlyGross,
            net: monthlyNet,
            tax: partnerIncomeTotals.tax
          });

          // Create summary with partner data
          const partnerSummary = {
            ...summaryData.data.data,
            monthlyGrossIncome: monthlyGross,
            monthlyNetIncome: monthlyNet
          };
          
          setSummary(partnerSummary);
          setExpenses(Array.isArray(expensesData.data?.data) ? expensesData.data.data : []);
          setDebts(Array.isArray(debtsData.data?.data) ? debtsData.data.data : []);
          setSavings(Array.isArray(savingsData.data?.data) ? savingsData.data.data : []);
          setAssets(Array.isArray(assetsData.data?.data) ? assetsData.data.data : []);
        } else {
          // Load user's own data OR combined data (for "Our Cash Flow")
          // ⚠️ NOTE: Income calculation is handled by the separate useEffect that watches 'incomes' state
          // This prevents race conditions and stale state when switching between modes
          const fetchOptions = isOurMode ? { includePartner: true } : {};
          const [expensesData, debtsData, savingsData, assetsData] = await Promise.all([
            getExpenses(fetchOptions),
            getDebts(fetchOptions),
            getSavings(fetchOptions),
            getAssets().catch(() => [])
          ]);

          // Calculate totals for summary (excluding income - that's handled separately)
          const totalMonthlyExpenses = Array.isArray(expensesData) ? expensesData.reduce((sum, expense) => sum + (expense.amount || 0), 0) : 0;
          const totalDebt = Array.isArray(debtsData) ? debtsData.reduce((sum, debt) => sum + (debt.amount || 0), 0) : 0;
          const totalMonthlyDebtPayments = Array.isArray(debtsData) ? debtsData.reduce((sum, debt) => sum + (debt.monthlyPayment || 0), 0) : 0;
          const totalSavings = Array.isArray(savingsData) ? savingsData.reduce((sum, saving) => sum + (saving.amount || 0), 0) : 0;
          const totalAssetValue = Array.isArray(assetsData) ? assetsData.reduce((total, asset) => total + (asset.currentValue || 0), 0) : 0;

          // Update summary - income will be calculated by the income useEffect
          setSummary(prev => ({
            monthlyGrossIncome: prev?.monthlyGrossIncome ?? 0, // Keep current or 0
            monthlyNetIncome: prev?.monthlyNetIncome ?? 0,     // Keep current or 0
            totalMonthlyExpenses,
            totalDebt,
            totalMonthlyDebtPayments,
            totalSavings,
            totalSavingsTarget: totalSavings * 1.5,
            totalAssetValue,
            netWorth: totalAssetValue - totalDebt
          }));

          console.log('✅ User/Combined data loaded (income will calculate next):', {
            mode: isOurMode ? 'Our Cash Flow' : 'My Cash Flow',
            currentIncomeInSummary: summary?.monthlyGrossIncome ?? 0
          });
          setExpenses(Array.isArray(expensesData) ? expensesData : []);
          setDebts(Array.isArray(debtsData) ? debtsData : []);
          setSavings(Array.isArray(savingsData) ? savingsData : []);
          setAssets(Array.isArray(assetsData) ? assetsData : []);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading cash flow data:', err);
        setError(err.message || 'Failed to load data. Please try again later.');
        setLoading(false);
      }
    };

    loadData();
  }, [activeViewTab, hasPartner, partnerPermissions]); // Removed fetchIncomes and incomes to avoid infinite loop

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Update URL based on tab selection
    const tabNames = ['', 'income', 'expenses', 'budget', 'debt', 'savings', 'assets'];
    const tabName = tabNames[tab];
    
    if (tab === 0) {
      // Summary tab - go to base cashflow route
      navigate('/cashflow');
    } else {
      // Specific tab - go to specific route
      navigate(`/cashflow/${tabName}`);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse flex flex-col items-center">
          <div className="rounded-full bg-gradient-to-r from-violet-300 to-indigo-300 h-12 w-12 mb-4"></div>
          <div className="h-4 w-32 bg-gradient-to-r from-violet-200 to-indigo-200 rounded"></div>
          <p className="mt-4 text-gray-500">
            Loading {isPartnerMode ? `${partnerDisplayName}'s` : 'your'} cash flow data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[400px] text-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-red-700 mb-2">Something went wrong</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { name: 'Summary', icon: '📊', color: 'from-blue-500 to-indigo-600' },
    { name: 'Income', icon: '💰', color: 'from-emerald-500 to-green-600' },
    { name: 'Expenses', icon: '💳', color: 'from-rose-500 to-red-600' },
    { name: 'Budget', icon: '📅', color: 'from-teal-500 to-cyan-600' },
    { name: 'Debt', icon: '🏦', color: 'from-orange-500 to-amber-600' },
    { name: 'Savings', icon: '🎯', color: 'from-violet-500 to-purple-600' },
    { name: 'Assets', icon: '🏠', color: 'from-emerald-500 to-teal-600' },
    { name: 'Investments', icon: '📈', color: 'from-indigo-500 to-purple-600' }
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-800 mr-4">
            Cash Flow
          </h1>
        </div>
      </div>

      {/* Tab Navigation - Sub-tabs (Summary, Income, Expenses, etc.) - Mobile Friendly */}
      <div className="cashflow-tab-navigation bg-gray-100/50 backdrop-blur-sm rounded-2xl p-1 sm:p-1.5 mb-4 sm:mb-6">
        {/* Mobile: Header with current tab and hamburger menu */}
        <div className="md:hidden flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">Cash Flow</span>
            <span className="text-gray-400 text-xs">•</span>
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-xs">{tabs[activeTab]?.icon}</span>
              <span className="text-xs font-medium text-gray-900 truncate">
                {tabs[activeTab]?.name || 'Select'}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowTabMenu(!showTabMenu)}
            className="rounded-lg hover:bg-gray-200/50 transition-colors flex-shrink-0 flex items-center justify-center"
            style={{ padding: '4.8px' }}
            aria-label="Toggle tab menu"
          >
            <Bars3Icon className="h-4 w-4 text-gray-600" />
          </button>
        </div>

        {/* Mobile: Hamburger Menu Dropdown */}
        {showTabMenu && (
          <div className="md:hidden mb-2 border-t border-gray-200 pt-2">
            <div className="grid grid-cols-2 gap-1.5">
              {tabs.map((tab, index) => (
                <button
                  key={tab.name}
                  onClick={() => {
                    handleTabChange(index);
                    setShowTabMenu(false);
                  }}
                  className={`
                    flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium 
                    transition-all text-left
                    ${activeTab === index 
                      ? `text-white bg-gradient-to-r ${tab.color} shadow-sm`
                      : 'text-gray-600 bg-white/50 hover:bg-white'
                    }
                  `}
                >
                  <span className="text-sm">{tab.icon}</span>
                  <span className="truncate">{tab.name}</span>
                  {activeTab === index && (
                    <span className="ml-auto text-xs">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Desktop: Full tab navigation - Max 2 rows (4 per row = 8 tabs) - Hide when hamburger menu is open on mobile */}
        {!showTabMenu && (
          <div className="relative">
            {/* Desktop: All tabs in 2-row grid (4 columns = 8 tabs) - Hidden on mobile */}
            <div className="hidden sm:grid sm:grid-cols-4 gap-1 sm:gap-1.5">
              {tabs.map((tab, index) => (
                <button
                  key={tab.name}
                  onClick={() => handleTabChange(index)}
                  className={`
                    flex items-center justify-center px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium 
                    transition-all duration-200 relative rounded-xl
                    ${activeTab === index 
                      ? `text-white bg-gradient-to-r ${tab.color} shadow-sm`
                      : 'text-gray-600 hover:text-gray-800 bg-transparent hover:bg-white/50'
                    }
                  `}
                  title={tab.name}
                >
                  <span className="mr-1 sm:mr-1.5 text-sm sm:text-base flex-shrink-0">{tab.icon}</span>
                  <span className="truncate text-center">{tab.name}</span>
                </button>
              ))}
            </div>
            
            {/* Mobile: Visible tabs in 2-row grid (3 columns = 6 tabs) + More button */}
            <div className="sm:hidden">
              {/* Visible tabs in 2-row grid (3 columns = 6 tabs max) */}
              <div className="grid grid-cols-3 gap-1">
                {tabs.slice(0, 6).map((tab, index) => (
                  <button
                    key={tab.name}
                    onClick={() => handleTabChange(index)}
                    className={`
                      flex items-center justify-center px-2 py-1.5 text-xs font-medium 
                      transition-all duration-200 relative rounded-xl
                      ${activeTab === index 
                        ? `text-white bg-gradient-to-r ${tab.color} shadow-sm`
                        : 'text-gray-600 hover:text-gray-800 bg-transparent hover:bg-white/50'
                      }
                    `}
                    title={tab.name}
                  >
                    <span className="mr-1 text-sm flex-shrink-0">{tab.icon}</span>
                    <span className="truncate text-center">{tab.name}</span>
                  </button>
                ))}
              </div>
              
              {/* More dropdown button if there are overflow tabs (tabs 7-8) - Mobile only */}
              {tabs.length > 6 && (
                <div className="mt-1">
                  {/* Dropdown menu for overflow tabs - appears ABOVE the More button */}
                  {showMoreTabsMenu && (
                    <div className="mb-1">
                      <div className="grid grid-cols-3 gap-1">
                        {tabs.slice(6).map((tab, overflowIndex) => {
                          const actualIndex = 6 + overflowIndex;
                          return (
                            <button
                              key={tab.name}
                              onClick={() => {
                                handleTabChange(actualIndex);
                                setShowMoreTabsMenu(false);
                              }}
                              className={`
                                flex items-center justify-center px-2 py-1.5 text-xs font-medium 
                                transition-all duration-200 relative rounded-xl
                                ${activeTab === actualIndex 
                                  ? `text-white bg-gradient-to-r ${tab.color} shadow-sm`
                                  : 'text-gray-600 hover:text-gray-800 bg-transparent hover:bg-white/50'
                                }
                              `}
                              title={tab.name}
                            >
                              <span className="mr-1 text-sm flex-shrink-0">{tab.icon}</span>
                              <span className="truncate text-center">{tab.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setShowMoreTabsMenu(!showMoreTabsMenu)}
                    className={`
                      w-full flex items-center justify-center px-2 py-0.5 text-xs font-medium 
                      transition-all duration-200 rounded-lg h-3
                      ${showMoreTabsMenu
                        ? 'text-white bg-gradient-to-r from-gray-500 to-gray-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 bg-transparent hover:bg-white/50'
                      }
                    `}
                  >
                    <span className="mr-1 text-[10px]">{showMoreTabsMenu ? 'Close' : 'More'}</span>
                    <ChevronDownIcon className={`h-2.5 w-2.5 transition-transform ${showMoreTabsMenu ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Partner View Selector - Mobile Friendly (using MobileViewSelector) */}
      {hasPartner && (
        <MobileViewSelector
          title="Views"
          currentView={activeViewTab}
          onViewChange={(view) => {
            setActiveViewTab(view);
          }}
          sharingButton={
            <button
              onClick={() => navigate('/profile')}
              className="rounded-lg bg-orange-600 hover:bg-orange-700 text-white transition-colors flex-shrink-0 flex items-center justify-center"
              style={{ padding: '4.8px' }}
              aria-label="Sharing Settings"
            >
              <ShareIcon className="h-4 w-4" />
            </button>
          }
          views={[
            {
              value: 'my',
              label: 'My Cash Flow',
              icon: <UserIcon className="h-5 w-5" />
            },
            {
              value: 'our',
              label: 'Our Cash Flow',
              icon: <UserGroupIcon className="h-5 w-5" />
            },
            {
              value: 'partner',
              label: `${partnerDisplayName}'s Cash Flow`,
              icon: <UserIcon className="h-5 w-5" />,
              disabled: !partnerPermissions?.hasFullCashflowAccess,
              title: partnerPermissions?.hasFullCashflowAccess 
                ? `View ${partnerDisplayName}'s cash flow`
                : 'Partner has not granted cashflow access'
            }
          ]}
        />
      )}

      {/* Content Area */}
      <div className="bg-white/95 backdrop-blur-md rounded-xl p-6 shadow-md border border-gray-100">
        {activeTab === 0 && (
          <SummarySection
            expenses={expenses}
            debts={debts}
            savings={savings}
            assets={assets}
            summary={summary}
            isPartnerMode={isPartnerMode}
            isOurMode={isOurMode}
            partnerName={partnerDisplayName}
          />
        )}
        {activeTab === 1 && (
          <IncomeSection 
            isPartnerMode={isPartnerMode}
            isOurMode={isOurMode}
            partnerName={partnerDisplayName}
          />
        )}
        {activeTab === 2 && (
          <ExpenseSection
            expenses={expenses}
            onExpensesChange={setExpenses}
            isPartnerMode={isPartnerMode}
            isOurMode={isOurMode}
            partnerName={partnerDisplayName}
            netMonthly={summary?.monthlyNetIncome || 0}
          />
        )}
        {activeTab === 3 && (
          <BudgetSection 
            income={incomes}
            expenses={expenses}
            isPartnerMode={isPartnerMode}
            partnerName={partnerDisplayName}
          />
        )}
        {activeTab === 4 && (
          <DebtSection
            debts={debts}
            onDebtsChange={setDebts}
            isPartnerMode={isPartnerMode}
            partnerName={partnerDisplayName}
          />
        )}
        {activeTab === 5 && (
          <SavingsSection
            savings={savings}
            onSavingsChange={setSavings}
            isPartnerMode={isPartnerMode}
            partnerName={partnerDisplayName}
          />
        )}
        {activeTab === 6 && (
          <AssetsSection
            assets={assets}
            onAssetsChange={setAssets}
            isPartnerMode={isPartnerMode}
            partnerName={partnerDisplayName}
          />
        )}

        {/* Investments Tab */}
        {activeTab === 7 && (
          <InvestmentsSection />
        )}
      </div>
    </div>
  );
};

export default CashFlowDashboard; 